import type { Experiment } from 'types';
import { useExperimentPlan } from '@/state/experiments/useExperimentPlan';

const DAY_MS = 24 * 60 * 60 * 1000;

type PlannedRunHintProps = {
  experiment: Experiment;
  /** Scheduled start, or null to start by hand, which is measured from now. */
  startAt: number | null;
  stopAt: number | null;
};

/**
 * What the chosen window can detect, beside the stop date that sets it.
 *
 * A stop date is where a run gets too short without anyone noticing: the date is picked
 * for a calendar reason, and nothing says that three weeks on this page can only ever
 * see a 20% lift. Shown only once a plan is saved, and computed from it, so it agrees
 * with the plan on the Brief tab. Like the card, nothing is estimated before that.
 */
export const PlannedRunHint = ({ experiment, startAt, stopAt }: PlannedRunHintProps) => {
  const { current, liveArms } = useExperimentPlan(experiment);
  if (stopAt === null || liveArms < 2 || current.dailySessions === null || current.controlRate === null) return null;

  const days = Math.floor((stopAt - (startAt ?? Date.now())) / DAY_MS);
  if (days <= 0) return null;

  const lift = current.liftIn(days);

  return (
    <p className="supporting">
      {lift === null
        ? `A ${days}-day run is too short to detect a lift at this website's usual traffic.`
        : `A ${days}-day run can detect lifts of about +${(lift * 100).toFixed(1)}% or more at the planned traffic.`}{' '}
      The plan is on the Brief tab.
    </p>
  );
};
